/**
 * Panel definitions for flex/intraday segment
 */
/*
 * Vertical layout (24-col grid):
 *   y=0   IDA1 / IDA2 / IDA3 (auction row)            h=12 → ends y=12
 *   y=12  Intraday Continuous 15m (left) / 1h (right) h=15 → ends y=27
 *   y=27  M7 Continuous Prices (left) / M7 Volumes    h=14 → ends y=41
 *   y=41  Auctions vs Day-Ahead (full width)           h=15 → ends y=56
 *   y=56  Intraday Indices (left) / Volume (right)     h=15 → ends y=71
 *   y=71  NL DA/IDC/imbalance/M7 enriched view         h=15 → ends y=86
 */
import { applyCountryFilterToPanels } from './countryContext';

const auctionPanels = [
  // Row 1: y=0 (IDA1, IDA2, IDA3)
  {
    id: 'epex-ida1-today',
    type: 'datasetChart',
    datasetId: 'epex-intraday-auctions',
    visualizationId: 'intraday-auctions-ida1-chart',
    gridPos: { x: 0, y: 0, w: 8, h: 12 },
  },
  {
    id: 'epex-ida2-today',
    type: 'datasetChart',
    datasetId: 'epex-intraday-auctions',
    visualizationId: 'intraday-auctions-ida2-chart',
    gridPos: { x: 8, y: 0, w: 8, h: 12 },
  },
  {
    id: 'epex-ida3-today',
    type: 'datasetChart',
    datasetId: 'epex-intraday-auctions',
    visualizationId: 'intraday-auctions-ida3-chart',
    gridPos: { x: 16, y: 0, w: 8, h: 12 },
  },
  // Row 2: y=12 (Intraday Continuous 15m / 1h side-by-side)
  {
    id: 'epex-intraday-continuous-15m',
    type: 'datasetChart',
    datasetId: 'epex-intraday-continuous',
    visualizationId: 'epex-intraday-continuous-chart-15m',
    gridPos: { x: 0, y: 12, w: 12, h: 15 },
  },
  {
    id: 'epex-intraday-continuous-1h',
    type: 'datasetChart',
    datasetId: 'epex-intraday-continuous',
    visualizationId: 'epex-intraday-continuous-chart-1h',
    gridPos: { x: 12, y: 12, w: 12, h: 15 },
  },
  // Row 4: y=41 (auctions vs day-ahead)
  {
    id: 'epex-intraday-vs-day-ahead',
    type: 'datasetChart',
    datasetId: 'epex-intraday-auctions',
    visualizationId: 'intraday-auctions-auctions-vs-day-ahead-chart',
    gridPos: { x: 0, y: 41, w: 24, h: 15 },
  },
];

// M7 panels are NOT passed through applyCountryFilterToPanels because
// the M7 dataset uses delivery_area (EIC code), not country.
const m7Panels = [
  // Row 3: y=27 (M7 price / volume side-by-side)
  {
    id: 'epex-m7-price',
    type: 'datasetChart',
    datasetId: 'epex-m7-intraday-prices-volumes',
    visualizationId: 'epex-m7-intraday-prices-volumes-price-chart',
    gridPos: { x: 0, y: 27, w: 12, h: 14 },
  },
  {
    id: 'epex-m7-volume',
    type: 'datasetChart',
    datasetId: 'epex-m7-intraday-prices-volumes',
    visualizationId: 'epex-m7-intraday-prices-volumes-volume-chart',
    gridPos: { x: 12, y: 27, w: 12, h: 14 },
  },
  // Fourth row: M7 VWAP indices (IDFull / ID3 / ID1) + traded volume.
  {
    id: 'epex-intraday-indices',
    type: 'datasetChart',
    datasetId: 'epex-intraday-indices',
    visualizationId: 'epex-intraday-indices-chart',
    gridPos: { x: 0, y: 56, w: 12, h: 15 },
  },
  {
    id: 'epex-intraday-indices-volume',
    type: 'datasetChart',
    datasetId: 'epex-intraday-indices',
    visualizationId: 'epex-intraday-indices-volume-chart',
    gridPos: { x: 12, y: 56, w: 12, h: 15 },
  },
];

const nlEnrichedPanels = [
  {
    id: 'nl-da-intraday-imbalance-m7',
    type: 'datasetChart',
    datasetId: 'epex-day-ahead-prices',
    visualizationId: 'nl-da-intraday-imbalance-m7',
    gridPos: { x: 0, y: 71, w: 24, h: 15 },
  },
];

const AUCTIONS_ROW_IDS = ['epex-ida1-today', 'epex-ida2-today', 'epex-ida3-today'];
const CONTINUOUS_ROW_IDS = ['epex-intraday-continuous-15m', 'epex-intraday-continuous-1h'];
const M7_ROW_IDS = ['epex-m7-price', 'epex-m7-volume'];

export const flexIntradayPanels = ({ country } = {}) => [
  ...applyCountryFilterToPanels(auctionPanels, country),
  ...m7Panels,
  ...(country === 'NL' ? nlEnrichedPanels : []),
];

export const flexIntradaySyncGroups = [
  {
    id: 'intraday-auctions-row',
    global: false,
    ids: AUCTIONS_ROW_IDS,
    syncAxes: { y: true },
  },
  {
    id: 'intraday-continuous-row',
    global: false,
    ids: CONTINUOUS_ROW_IDS,
    syncDateRange: true,
  },
  {
    id: 'intraday-m7-row',
    global: false,
    ids: M7_ROW_IDS,
    // Catalog filter ids: `delivery_area` merged into `bidding_zone` (one dimension, three
    // encodings) and `product` split per auction, since one id named three unrelated
    // vocabularies. Sync matches on the canonical id, not the deprecated query alias.
    syncFilters: ['bidding_zone', 'intraday_contract'],
    syncDateRange: true,
  },
];
